import React from 'react';
import { Icon } from './Icon.jsx';

const SIZES = {
  sm: { fontSize: 'var(--text-sm)',  padding: '8px 14px',  gap: 6,  icon: 16, radius: 'var(--radius-sm)' },
  md: { fontSize: 'var(--text-base)', padding: '11px 20px', gap: 8,  icon: 18, radius: 'var(--radius-md)' },
  lg: { fontSize: 'var(--text-lg)',  padding: '15px 28px', gap: 10, icon: 20, radius: 'var(--radius-lg)' },
};

const VARIANTS = {
  primary:   { bg: 'var(--primary)',   fg: 'var(--on-primary)',   bd: 'transparent',        hover: 'var(--primary-hover)' },
  secondary: { bg: 'var(--secondary)', fg: 'var(--on-secondary)', bd: 'transparent',        hover: 'var(--secondary-hover)' },
  accent:    { bg: 'var(--accent)',    fg: 'var(--on-accent)',    bd: 'transparent',        hover: 'var(--accent-deep)' },
  outline:   { bg: 'transparent',      fg: 'var(--text-strong)',  bd: 'var(--border-strong)', hover: 'var(--surface-hover)' },
  ghost:     { bg: 'transparent',      fg: 'var(--text-strong)',  bd: 'transparent',        hover: 'var(--surface-hover)' },
  danger:    { bg: 'var(--danger)',    fg: 'var(--on-primary)',   bd: 'transparent',        hover: 'var(--dala-red-deep)' },
};

/**
 * Button — the friendly, rounded Squarehead action. `pop` gives it the chunky
 * "sticker" drop shadow that presses down on click.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  pop = false,
  block = false,
  disabled = false,
  iconLeft,
  iconRight,
  style,
  ...rest
}) {
  const s = SIZES[size] || SIZES.md;
  const v = VARIANTS[variant] || VARIANTS.primary;
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);

  const base = {
    display: block ? 'flex' : 'inline-flex',
    width: block ? '100%' : undefined,
    alignItems: 'center',
    justifyContent: 'center',
    gap: s.gap,
    fontFamily: 'var(--font-sans)',
    fontWeight: 'var(--fw-bold)',
    fontSize: s.fontSize,
    lineHeight: 1,
    letterSpacing: 'var(--ls-wide)',
    color: v.fg,
    background: hover && !disabled ? v.hover : v.bg,
    border: `2px solid ${v.bd === 'transparent' ? (hover && !disabled ? v.hover : v.bg) : v.bd}`,
    borderRadius: s.radius,
    padding: s.padding,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    boxShadow: pop && !disabled ? (active ? 'var(--shadow-xs)' : 'var(--shadow-pop)') : 'none',
    transform: pop && active && !disabled ? 'translateY(6px)' : 'translateY(0)',
    transition: `background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)`,
    userSelect: 'none',
    ...style,
  };

  return (
    <button
      type="button"
      disabled={disabled}
      style={base}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={s.icon} />}
      {children}
      {iconRight && <Icon name={iconRight} size={s.icon} />}
    </button>
  );
}
